// src/components/pages/b2b/ESG/EsgMissionVision.js
import React from "react";
import { Box, Typography } from "@mui/material";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";

const mainColor = "#005F91";

const ESG_MISSION_VISION_DATA = {
  sectionTitle: {
    ZH: "我們的使命與願景",
    EN: "Mission and Vision",
    JP: "私たちの使命とビジョン",
  },

  subtitle: {
    ZH: "食品衛生安全・提升生活品質・守護環境",
    EN: "Food Safety, Quality of Life, and Environmental Protection",
    JP: "食品衛生安全・生活品質の向上・環境保護",
  },

  intro: {
    ZH: [
      "台灣水禾實業專注於食品衛生、病媒防治與環境清潔三大領域，致力於提供更專業、更安全、更永續的整體衛生管理解決方案。",
      "我們相信企業的價值，不只來自營運成長，更來自對社會、環境與公共健康的長期責任。",
      "台灣水禾以「提升生活品質、守護公共健康、實踐永續發展」作為核心理念，持續落實 ESG 精神，推動更安心、更潔淨的生活環境。",
    ],

    EN: [
      "Taiwan SEHO specializes in food hygiene, pest control, and environmental cleaning services, providing more professional, safer, and sustainable integrated hygiene management solutions.",
      "We believe that the value of a company comes not only from business growth, but also from its long-term responsibility toward society, the environment, and public health.",
      "With the core philosophy of improving quality of life, protecting public health, and promoting sustainable development, Taiwan SEHO continues to implement ESG principles and create safer and cleaner living environments.",
    ],

    JP: [
      "台湾水禾実業は、食品衛生、害虫防除、環境清掃の三つの分野を中心に、より専門的で安全かつ持続可能な総合衛生管理ソリューションを提供しています。",
      "私たちは、企業の価値は事業成長だけでなく、社会・環境・公共衛生に対する長期的な責任から生まれると考えています。",
      "『生活品質の向上』『公共衛生の保護』『持続可能な発展の実践』を核心理念として、ESG精神を継続的に実践し、より安全で清潔な生活環境づくりを推進しています。",
    ],
  },

  rows: [
    {
      key: "mission",

      title: {
        ZH: "我們的使命",
        EN: "Our\nMission",
        JP: "私たちの使命",
      },

      text: {
        ZH: "提升環境衛生品質，守護每一個生活場域。透過專業管理、科學方法與持續創新，為家庭、餐飲、食品產業與各類公共空間建立更安全、健康的環境標準。",
        EN: "Improving hygiene standards for safer, healthier homes, businesses, and public spaces.",
        JP: "環境衛生の品質を高め、あらゆる生活空間を守ります。専門的な管理、科学的手法、継続的な革新により、家庭、飲食店、食品産業、公共空間に、より安全で健康的な環境基準を築きます。",
      },
    },

    {
      key: "vision",

      title: {
        ZH: "我們的願景",
        EN: "Our\nVision",
        JP: "私たちのビジョン",
      },

      text: {
        ZH: "以專業服務與永續思維，期許成為台灣永續衛生與環境管理的長期合作夥伴，協助企業與社會建立更高品質的環境衛生文化，共同邁向健康、潔淨且永續的未來。",
        EN: "With professional service and sustainable thinking, we support cleaner, healthier, and more sustainable environments.",
        JP: "専門サービスと持続可能な視点で、より清潔で健康的な環境づくりを支えます。",
      },
    },
  ],
};

function getLocalized(value, lang, fallback = "") {
  if (!value) return fallback;

  if (typeof value === "string") {
    return value;
  }

  return value[lang] || value.ZH || value.EN || value.JP || fallback;
}

function getLocalizedList(value, lang) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value;
  }

  return value[lang] || value.ZH || value.EN || value.JP || [];
}

function getFontFamily(fonts, lang) {
  if (typeof fonts === "string") {
    return fonts;
  }

  return (
    fonts?.[lang] ||
    fonts?.ZH ||
    '"LINE Seed TW","Noto Sans TC",system-ui'
  );
}

function MissionVisionRow({ row, lang, fontFamily }) {
  const title = getLocalized(row.title, lang);
  const text = getLocalized(row.text, lang);

  return (
    <Box
      sx={{
        display: "grid",

        gridTemplateColumns: {
          xs: "96px minmax(0, 1fr)",
          sm: "122px minmax(0, 1fr)",
          md: "140px minmax(0, 1fr)",
        },

        alignItems: "center",

        columnGap: {
          xs: 0,
          md: 0,
        },

        rowGap: 0,

        maxWidth: 1100,

        mx: "auto",

        mb: {
          xs: 3.5,
          sm: 4,
          md: 5,
        },

        "&:last-of-type": {
          mb: 0,
        },
      }}
    >
      {/* LEFT WHITE HEXAGON LABEL */}
      <Box
        sx={{
          width: {
            xs: 96,
            sm: 118,
            md: 130,
          },

          height: {
            xs: 96,
            sm: 118,
            md: 130,
          },

          mx: 0,

          bgcolor: "#ffffff",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          color: mainColor,

          clipPath:
            "polygon(50% 0%, 96% 25%, 96% 75%, 50% 100%, 4% 75%, 4% 25%)",

          boxShadow:
            "0 12px 30px rgba(0, 0, 0, 0.08)",

          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: fontFamily,
            fontWeight: 700,

            fontSize: {
              xs: 13,
              sm: 16,
              md: 20,
            },

            lineHeight: 1.25,

            textAlign: "center",
            whiteSpace: "pre-line"
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* RIGHT OUTLINED ARROW TEXT BOX */}
      <Box
        sx={{
          position: "relative",

          minHeight: {
            xs: 96,
            sm: 118,
            md: 130,
          },

          display: "flex",
          alignItems: "center",

          px: {
            xs: 2,
            sm: 3,
            md: 4.5,
          },

          py: {
            xs: 1.5,
            sm: 1.75,
            md: 2,
          },

          ml: {
            xs: -1,
            sm: -1.25,
            md: -1.5,
          },

          color: "#ffffff",

          backgroundColor: "transparent",

          boxSizing: "border-box",
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 1000 100"
          preserveAspectRatio="none"
          aria-hidden="true"
          sx={{
            position: "absolute",

            inset: 0,

            width: "100%",
            height: "100%",

            overflow: "visible",

            display: "block",

            pointerEvents: "none",
          }}
        >
          <path
            d="M 0 0 L 36 16 L 944 16 L 1000 50 L 944 84 L 36 84 L 0 100"
            fill="none"
            stroke="rgba(255,255,255,0.68)"
            strokeWidth="1.4"
            vectorEffect="non-scaling-stroke"
          />
        </Box>

        <Box
          sx={{
            position: "absolute",

            inset: 0,

            display: "none",

            border: "1px solid rgba(255,255,255,0.68)",

            pointerEvents: "none",
          }}
        />

        <Typography
          sx={{
            position: "relative",

            zIndex: 1,

            fontFamily: fontFamily,

            fontWeight: 400,

            fontSize: {
              xs: 10.5,
              sm: 13,
              md: 17,
            },

            lineHeight: {
              xs: 1.55,
              sm: 1.65,
              md: 1.85,
            },

            color: "#ffffff",
          }}
        >
          {text}
        </Typography>
      </Box>
    </Box>
  );
}

export default function EsgMissionVision({
  lang = "ZH",
  fonts,
}) {
  const fontFamily = getFontFamily(fonts, lang);

  const sectionTitle = getLocalized(
    ESG_MISSION_VISION_DATA.sectionTitle,
    lang,
  );

  const subtitle = getLocalized(
    ESG_MISSION_VISION_DATA.subtitle,
    lang,
  );

  const introLines = getLocalizedList(
    ESG_MISSION_VISION_DATA.intro,
    lang,
  );

  const rows = ESG_MISSION_VISION_DATA.rows;

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        bgcolor: "#ffffff",
      }}
    >
      {/* TOP WHITE CONTENT */}
      <Box
        sx={{
          pt: {
            xs: 3,
            md: 0,
            lg: 0
          },

          pb: {
            xs: 4,
            md: 5,
          },

          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: fontFamily,

            fontWeight: 700,

            fontSize: {
              xs: 24,
              md: 40,
            },

            lineHeight: 1.35,

            color: mainColor,

            mb: {
              xs: 4,
              md: 5,
            },
          }}
        >
          {sectionTitle}
        </Typography>

        <Box
          sx={{
            maxWidth: {
              xs: "100%",
              md: 1100,
            },

            mx: "auto",

            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: fontFamily,

              fontWeight: 700,

              fontSize: {
                xs: 17,
                md: 21,
              },

              lineHeight: 1.55,

              color: mainColor,

              mb: {
                xs: 1.5,
                md: 2,
              },
            }}
          >
            {subtitle}
          </Typography>

          <Typography
            sx={{
              fontFamily: fontFamily,

              fontWeight: 400,

              fontSize: {
                xs: 14,
                md: 18,
              },

              lineHeight: {
                xs: 1.85,
                md: 1.9,
              },

              color: mainColor,
            }}
          >
            {introLines.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < introLines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </Typography>
        </Box>
      </Box>

      {/* BOTTOM TEAL MISSION / VISION AREA */}
      <Box
        sx={{
          width: "100vw",

          ml: "calc(50% - 50vw)",

          bgcolor: "#005F91",

          pt: {
            xs: 5,
            md: 6,
          },

          pb: {
            xs: 5.5,
            md: 7,
          },

        }}
      >
        <Box
          sx={{
            maxWidth: 980,

            mx: "auto",

            px: {
              xs: 2,
              md: 3,
            },
          }}
        >
          {rows.map((row) => (
            <MissionVisionRow
              key={row.key}
              row={row}
              lang={lang}
              fontFamily={fontFamily}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}